/**
 * Test-fixture medical-history answers for one section.
 *
 * The Section Review screen has no linking path: the only way in is a Medical
 * History overview row whose section already reads as complete, and section
 * completeness is derived from the patient record the API returns rather than
 * from any UI flag (`sectionRegistry.ts` runs one `calculate*Completion` per
 * section over that record). Visual-evidence capture runs with the auth gate
 * dismissed, so `usePatient` has no record at all, every section reads 0% and
 * the Section Review route could not be captured.
 *
 * Enabled with `?test=1&test_medical_history_complete_section=sleep-activity`.
 * The fixture supplies the answers for exactly one section, in the shape the
 * patient endpoint returns them: the `lifestyle` fields the Sleep & Activity
 * flow itself writes (see `transformSleepActivityStateToRequest` for the PATCH
 * and `SleepActivityContext` for the hydration of the same fields off the GET).
 * The two screens that read the record for these surfaces merge it over what
 * `usePatient` returned; everything downstream — completion percent, the
 * overview recap, the review rows — is then computed by product code from that
 * payload, the same code path a real record takes. Without the param the merge
 * returns the record untouched, so there is no second product behaviour to
 * maintain.
 *
 * Sleep & Activity is the only section whose fields no other completion
 * calculator reads, so the fixture cannot complete a second section by
 * accident. That matters for the `medical-history-family-history-complete`
 * route, which needs its own section to stay incomplete so the overview row
 * opens the question flow.
 */

export type CompletableMedicalHistorySection = "sleep-activity";

const SLEEP_ACTIVITY_LIFESTYLE = {
  minHoursOfSleep: 7,
  maxHoursOfSleep: 8,
  isExercising: true,
  minHoursOfExercise: 3,
  maxHoursOfExercise: 5,
};

export const COMPLETE_MEDICAL_HISTORY_SECTION: CompletableMedicalHistorySection | null =
  (() => {
    if (typeof window === "undefined") return null;
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get("test") !== "1") return null;
      const requested = params.get("test_medical_history_complete_section");
      return requested === "sleep-activity" ? requested : null;
    } catch {
      return null;
    }
  })();

// A signed-out capture has no record to merge onto. One shared empty base keeps
// the merged result referentially stable across renders, the way a cached query
// result is.
const NO_PATIENT_RECORD: Record<string, unknown> = {};
const mergedRecords = new WeakMap<object, Record<string, unknown>>();

export function withCompleteMedicalHistorySection<T>(patient: T): T {
  if (!COMPLETE_MEDICAL_HISTORY_SECTION) return patient;

  const base = (patient ?? NO_PATIENT_RECORD) as Record<string, unknown>;
  const merged = mergedRecords.get(base);
  if (merged) return merged as T;

  const next = {
    ...base,
    lifestyle: {
      ...((base.lifestyle as Record<string, unknown> | undefined) ?? {}),
      ...SLEEP_ACTIVITY_LIFESTYLE,
    },
  };
  mergedRecords.set(base, next);
  return next as T;
}
